// src/lib/seo/schema.ts
//
// JSON-LD structured data builders for the public marketing surface.
//
// GROUND RULE: every field here must be backed by something real in this repo
// or in the database. Structured data that asserts facts the site cannot
// support is worse than no structured data at all — it risks a manual action
// and, for AI engines, launders invented claims into answers.
//
// Deliberately NOT emitted, and why:
//
//   aggregateRating / reviewCount
//     products_public.average_rating and .review_count are 0 for all 12 active
//     products — schema defaults, not real reviews. Omitted entirely rather
//     than emitted as zeroes.
//
//   sameAs
//     No social profile URL exists anywhere in this codebase. Nothing to cite.
//
//   Organization.address / legalName
//     Terms and Privacy list TWO entities — "16349684 Canada Inc. (dba Fyrris)"
//     in Bromont QC, and "Fyrris Inc." in Las Vegas NV. Picking one as *the*
//     organization is a legal//business call, not a technical one. Flagged for
//     the team; add here once decided.
//
//   SoftwareApplication.offers
//     Pricing is tiered subscription (see /retailers/pricing). A single price
//     node would misrepresent it.
//
//   SoftwareApplication.downloadUrl / installUrl
//     VITE_APPSTORE_URL is "https://apps.apple.com/app/fyrris" — no numeric app
//     ID, so almost certainly still a placeholder. Omitted until confirmed live.

import routeData from "./public-routes.json";

// Read from the shared route data rather than declaring a second origin — the
// two drifted apart the moment the canonical host changed apex -> www.
export const SITE_ORIGIN: string = routeData.origin;

/** Stable @id so other nodes can reference the organization by URI. */
export const ORG_ID = `${SITE_ORIGIN}/#organization`;

export interface OrganizationInput {
  /** Locale-appropriate one-line description of the company. */
  description: string;
}

export function organizationSchema({ description }: OrganizationInput) {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": ORG_ID,
    name: "Fyrris",
    // Confirmed by the team as the canonical entity for structured data. The
    // US entity ("Fyrris Inc.", Las Vegas NV) also appears in Terms and
    // Privacy; it is intentionally not the one represented here.
    legalName: "16349684 Canada Inc.",
    url: `${SITE_ORIGIN}/`,
    logo: {
      "@type": "ImageObject",
      url: `${SITE_ORIGIN}/fyrris-logo.png`,
    },
    address: {
      "@type": "PostalAddress",
      streetAddress: "23 Rue des Lilas",
      addressLocality: "Bromont",
      addressRegion: "QC",
      postalCode: "J2L 1M4",
      addressCountry: "CA",
    },
    description,
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer support",
      email: "support@fyrris.com",
      // Both legal entities publish English and French contact pages.
      availableLanguage: ["en", "fr"],
    },
  };
}

export interface SoftwareApplicationInput {
  description: string;
}

/**
 * Represents the Fyrris platform as marketed on fyrris.com: a retailer-facing
 * AR product for the hearth/patio/BBQ trade, delivered as iOS + Android
 * consumer apps plus a web portal.
 */
export function softwareApplicationSchema({ description }: SoftwareApplicationInput) {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "@id": `${SITE_ORIGIN}/#software`,
    name: "Fyrris",
    url: `${SITE_ORIGIN}/`,
    applicationCategory: "BusinessApplication",
    operatingSystem: "iOS, Android, Web",
    description,
    publisher: { "@id": ORG_ID },
    featureList: [
      "Augmented reality product visualization",
      "In-home fireplace, BBQ and patio product preview",
      "Retailer geolocation and showroom routing",
      "3D product catalogue management",
    ],
  };
}

// Product schema for /demo/:productId lives in scripts/product-schema.mjs, not
// here. It is built at prerender time from the Supabase enumeration so that
// src/pages/Demo.tsx keeps its existing data-fetching — pulling a manufacturer
// name for Product.brand inside that page would have meant editing the AR
// viewer's Supabase logic. One builder, one home, no duplication.
